// src/stores/gamificationStore.js - Zustand-based Gamification Store
import { create } from 'zustand';
import { persist, devtools } from 'zustand/middleware';
import { MINDEASE_ACHIEVEMENTS } from '../data/mindeaseAchievements.js';
import { db } from '../services/config/firebase';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { triggerQuickCelebration } from '../components/shared/CelebrationAnimation';

const GAMIFICATION_VERSION = '2.0';

// Every badge the store can award lives in data/mindeaseAchievements.js.
const VIBE_ACHIEVEMENTS = { ...MINDEASE_ACHIEVEMENTS };

// LEVEL SYSTEM
const LEVEL_SYSTEM = {
  1: { name: 'Noticing', emoji: '🌱', xpRequired: 100, vibe: 'You can tell when the alarm has gone off.' },
  2: { name: 'Naming It', emoji: '🔍', xpRequired: 250, vibe: 'You know which channel fires first in you.' },
  3: { name: 'Out-Breath', emoji: '🌬️', xpRequired: 500, vibe: 'You know which half of the breath is the brake.' },
  4: { name: 'Ledger Keeper', emoji: '📝', xpRequired: 1000, vibe: 'Forecasts get a date and a verdict.' },
  5: { name: 'Rung by Rung', emoji: '🪜', xpRequired: 1750, vibe: 'You go back in, one rung at a time, with a stop rule.' },
  6: { name: 'Night Watch', emoji: '🌙', xpRequired: 2750, vibe: 'Nothing thought at 3am gets a vote until morning.' },
  7: { name: 'Camera Out', emoji: '👁️', xpRequired: 4000, vibe: 'Attention on the other person, three things about them.' },
  8: { name: 'Good Enough', emoji: '⚖️', xpRequired: 5500, vibe: 'A standard with a yes in it, and the note before question 9.' },
  9: { name: 'Navigator', emoji: '🧭', xpRequired: 7500, vibe: 'The kit is yours and you know which tools are for which day.' },
  10: { name: 'Dial, Not Switch', emoji: '🎚️', xpRequired: 10000, vibe: 'Nothing turns the alarm off. You can turn it down, and you know who to tell.' },
};

// Helper to calculate level from XP
const calculateLevel = (xp) => {
  let level = 1;
  for (let i = 10; i >= 1; i--) {
    if (xp >= LEVEL_SYSTEM[i].xpRequired) {
      level = i;
      break;
    }
  }
  return level;
};

// Helper to get current user ID
const getCurrentUserId = () => {
  try {
    const authData = localStorage.getItem('vibe_user_identity');
    if (authData) {
      const parsed = JSON.parse(authData);
      return parsed.userId;
    }
    return null; // Guest user
  } catch (error) {
    console.warn('Error getting user ID:', error);
    return null;
  }
};

// Firebase sync service
const syncToFirebase = async (userId, data) => {
  if (!userId) return; // Don't sync for guest users

  try {
    await setDoc(
      doc(db, 'users', userId, 'gamification', 'progress'),
      {
        ...data,
        lastUpdated: new Date().toISOString(),
        version: GAMIFICATION_VERSION,
      },
      { merge: true }
    );
  } catch (error) {
    console.error('Firebase sync failed:', error);
  }
};

const loadFromFirebase = async (userId) => {
  if (!userId) return null;

  try {
    const docRef = doc(db, 'users', userId, 'gamification', 'progress');
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? docSnap.data() : null;
  } catch (error) {
    console.error('Firebase load failed:', error);
    return null;
  }
};

// Create the Zustand store
// The store used to persist under the sibling app's key. Carry it over once,
// then forget the old key, so an existing account keeps its XP and streak.
const PERSIST_KEY = 'mindease-vibe-gamification';
const LEGACY_PERSIST_KEY = 'friendships-vibe-gamification';
try {
  if (typeof localStorage !== 'undefined' && localStorage.getItem(PERSIST_KEY) === null) {
    const legacy = localStorage.getItem(LEGACY_PERSIST_KEY);
    if (legacy !== null) {
      localStorage.setItem(PERSIST_KEY, legacy);
      localStorage.removeItem(LEGACY_PERSIST_KEY);
    }
  }
} catch {
  /* storage unavailable */
}

export const useGamificationStore = create(
  devtools(
    persist(
      (set, get) => ({
        // State
        totalXP: 0,
        currentLevel: 1,
        completedModules: [],
        completedTools: [],
        unlockedAchievements: [],
        pendingAchievements: [],
        currentStreak: 0,
        longestStreak: 0,
        helpedOthers: 0,
        lastActivity: null,
        isInitialized: false,
        savedInsights: [], // User's personal insight journal

        // Computed values (getters)
        getCurrentLevelData: () => {
          const level = get().currentLevel;
          return LEVEL_SYSTEM[level] || LEVEL_SYSTEM[1];
        },

        getXPToNextLevel: () => {
          const state = get();
          const nextLevel = state.currentLevel + 1;
          if (nextLevel > 10) return 0;
          return LEVEL_SYSTEM[nextLevel].xpRequired - state.totalXP;
        },

        // Actions
        addXP: (amount, reason = '') => {
          const state = get();
          const newXP = state.totalXP + amount;
          const newLevel = calculateLevel(newXP);
          const leveledUp = newLevel > state.currentLevel;

          set({
            totalXP: newXP,
            currentLevel: newLevel,
            lastActivity: new Date().toISOString(),
          });

          // Trigger celebration
          if (leveledUp) {
            triggerQuickCelebration(`Level ${newLevel}! ${LEVEL_SYSTEM[newLevel].emoji}`);
          }

          // Sync to Firebase (async, non-blocking)
          const userId = getCurrentUserId();
          if (userId) {
            syncToFirebase(userId, {
              totalXP: newXP,
              currentLevel: newLevel,
              lastActivity: new Date().toISOString(),
            });
          }
        },

        completeModule: (moduleId) => {
          const state = get();

          // Don't complete twice
          if (state.completedModules.includes(moduleId)) {
            return;
          }

          const newCompleted = [...state.completedModules, moduleId];

          set({
            completedModules: newCompleted,
            lastActivity: new Date().toISOString(),
          });

          // Award XP
          get().addXP(100, `Completed Module ${moduleId}`);

          // Check for achievements
          get().checkModuleAchievements(newCompleted);

          // Sync to Firebase
          const userId = getCurrentUserId();
          if (userId) {
            syncToFirebase(userId, {
              completedModules: newCompleted,
              lastActivity: new Date().toISOString(),
            });
          }
        },

        unlockAchievement: (achievementId) => {
          const state = get();

          if (state.unlockedAchievements.includes(achievementId)) {
            return;
          }

          const achievement = VIBE_ACHIEVEMENTS[achievementId];
          if (!achievement) return;

          set({
            unlockedAchievements: [...state.unlockedAchievements, achievementId],
            pendingAchievements: [...state.pendingAchievements, achievementId],
          });

          // Award achievement XP
          if (achievement.xp) {
            get().addXP(achievement.xp, `Unlocked: ${achievement.name}`);
          }

          // Celebration
          triggerQuickCelebration(`${achievement.emoji} ${achievement.name}`);

          // Sync to Firebase
          const userId = getCurrentUserId();
          if (userId) {
            syncToFirebase(userId, {
              unlockedAchievements: get().unlockedAchievements,
              pendingAchievements: get().pendingAchievements,
            });
          }
        },

        updateStreak: () => {
          const state = get();
          const now = new Date();
          const lastActivity = state.lastActivity ? new Date(state.lastActivity) : null;

          if (!lastActivity) {
            // First activity
            set({
              currentStreak: 1,
              longestStreak: 1,
              lastActivity: now.toISOString(),
            });
            return;
          }

          const daysSinceLastActivity = Math.floor(
            (now - lastActivity) / (1000 * 60 * 60 * 24)
          );

          let newStreak = state.currentStreak;

          if (daysSinceLastActivity === 0) {
            // Same day - no change
            return;
          } else if (daysSinceLastActivity === 1) {
            // Consecutive day - increment
            newStreak = state.currentStreak + 1;
          } else {
            // Streak broken - reset
            newStreak = 1;
          }

          const newLongestStreak = Math.max(newStreak, state.longestStreak);

          set({
            currentStreak: newStreak,
            longestStreak: newLongestStreak,
            lastActivity: now.toISOString(),
          });

          // Sync to Firebase
          const userId = getCurrentUserId();
          if (userId) {
            syncToFirebase(userId, {
              currentStreak: newStreak,
              longestStreak: newLongestStreak,
              lastActivity: now.toISOString(),
            });
          }
        },

        helpSomeone: () => {
          set((state) => ({
            helpedOthers: state.helpedOthers + 1,
          }));

          get().addXP(25, 'Helped someone');
        },

        clearPendingAchievements: () => {
          set({ pendingAchievements: [] });
        },

        // Insight Journal Actions
        saveInsight: (insight) => {
          const state = get();
          const newInsight = {
            id: Date.now().toString(),
            timestamp: new Date().toISOString(),
            ...insight,
          };

          const updatedInsights = [newInsight, ...state.savedInsights];

          set({
            savedInsights: updatedInsights,
            lastActivity: new Date().toISOString(),
          });

          // Award XP for reflection
          get().addXP(10, 'Saved an insight');

          // Sync to Firebase
          const userId = getCurrentUserId();
          if (userId) {
            syncToFirebase(userId, {
              savedInsights: updatedInsights,
              lastActivity: new Date().toISOString(),
            });
          }

          triggerQuickCelebration('💡 Insight Saved!');
        },

        deleteInsight: (insightId) => {
          const state = get();
          const updatedInsights = state.savedInsights.filter((i) => i.id !== insightId);

          set({ savedInsights: updatedInsights });

          // Sync to Firebase
          const userId = getCurrentUserId();
          if (userId) {
            syncToFirebase(userId, { savedInsights: updatedInsights });
          }
        },

        updateInsight: (insightId, updates) => {
          const state = get();
          const updatedInsights = state.savedInsights.map((i) =>
            i.id === insightId ? { ...i, ...updates, updatedAt: new Date().toISOString() } : i
          );

          set({ savedInsights: updatedInsights });

          // Sync to Firebase
          const userId = getCurrentUserId();
          if (userId) {
            syncToFirebase(userId, { savedInsights: updatedInsights });
          }
        },

        checkModuleAchievements: (completedModules) => {
          // Check for milestone achievements
          if (completedModules.length === 1) {
            get().unlockAchievement('first_module');
          }
          if (completedModules.length === 3) {
            get().unlockAchievement('three_modules');
          }
          if (completedModules.length >= 8) {
            get().unlockAchievement('all_modules');
          }
        },

        checkToolAchievements: (completedTools) => {
          // Check for tool milestone achievements
          if (completedTools.length === 1) {
            get().unlockAchievement('first_tool');
          }
          if (completedTools.length === 3) {
            get().unlockAchievement('three_tools');
          }
          if (completedTools.length >= 6) {
            get().unlockAchievement('all_tools');
          }
        },

        completeTool: (toolId) => {
          const state = get();

          // Don't complete twice
          if (state.completedTools.includes(toolId)) {
            return;
          }

          const newCompleted = [...state.completedTools, toolId];

          set({
            completedTools: newCompleted,
            lastActivity: new Date().toISOString(),
          });

          // Award XP
          get().addXP(50, `Completed Tool ${toolId}`);

          // Check for achievements
          get().checkToolAchievements(newCompleted);

          // Sync to Firebase
          const userId = getCurrentUserId();
          if (userId) {
            syncToFirebase(userId, {
              completedTools: newCompleted,
              lastActivity: new Date().toISOString(),
            });
          }
        },

        syncModuleProgress: (moduleId, isCompleted) => {
          if (isCompleted) {
            get().completeModule(moduleId);
          }
        },

        // Initialize from Firebase
        initializeFromFirebase: async () => {
          const userId = getCurrentUserId();
          if (!userId) {
            set({ isInitialized: true });
            return;
          }

          try {
            const firebaseData = await loadFromFirebase(userId);

            if (firebaseData) {
              set({
                totalXP: firebaseData.totalXP || 0,
                currentLevel: firebaseData.currentLevel || 1,
                completedModules: firebaseData.completedModules || [],
                completedTools: firebaseData.completedTools || [],
                unlockedAchievements: firebaseData.unlockedAchievements || [],
                currentStreak: firebaseData.currentStreak || 0,
                longestStreak: firebaseData.longestStreak || 0,
                helpedOthers: firebaseData.helpedOthers || 0,
                lastActivity: firebaseData.lastActivity || null,
                savedInsights: firebaseData.savedInsights || [],
                isInitialized: true,
              });
            } else {
              set({ isInitialized: true });
            }
          } catch (error) {
            console.error('Failed to initialize from Firebase:', error);
            set({ isInitialized: true });
          }
        },

        // Clear all data (for logout)
        clearUserData: () => {
          set({
            totalXP: 0,
            currentLevel: 1,
            completedModules: [],
            completedTools: [],
            unlockedAchievements: [],
            pendingAchievements: [],
            currentStreak: 0,
            longestStreak: 0,
            helpedOthers: 0,
            lastActivity: null,
            savedInsights: [],
          });
        },

        // Constants (for backward compatibility)
        VIBE_ACHIEVEMENTS,
        LEVEL_SYSTEM,
      }),
      {
        name: PERSIST_KEY,
        partialize: (state) => ({
          totalXP: state.totalXP,
          currentLevel: state.currentLevel,
          completedModules: state.completedModules,
          completedTools: state.completedTools,
          unlockedAchievements: state.unlockedAchievements,
          currentStreak: state.currentStreak,
          longestStreak: state.longestStreak,
          helpedOthers: state.helpedOthers,
          lastActivity: state.lastActivity,
          savedInsights: state.savedInsights,
        }),
      }
    ),
    { name: 'GamificationStore' }
  )
);

// Hook to initialize on app start
export const initializeGamification = () => {
  useGamificationStore.getState().initializeFromFirebase();
};

// Backward compatibility exports
export const useGamification = useGamificationStore;
export const useVibeGamification = useGamificationStore;
